/**
 * Сохранение и загрузка состояния пазла (где каждый кусок: лоток / доска, координаты, зафиксирован ли).
 * Ключ: puzzleId + difficulty. Используется при dragend, snap, возврате в лоток и уходе со сцены.
 */

import { getJSON, setJSON, remove } from './Storage';

export type PieceWhere = 'tray' | 'board';

export interface PieceStateEntry {
  where: PieceWhere;
  x?: number;
  y?: number;
  placedCorrect: boolean;
}

export type PuzzleStateMap = Record<number, PieceStateEntry>;

const KEY_PREFIX = 'puzzle-state-';
const TRAY_ORDER_KEY_PREFIX = 'puzzle-tray-order-';

function storageKey(puzzleId: string, difficulty: number): string {
  return `${KEY_PREFIX}${puzzleId}-${difficulty}`;
}

function trayOrderStorageKey(puzzleId: string, difficulty: number): string {
  return `${TRAY_ORDER_KEY_PREFIX}${puzzleId}-${difficulty}`;
}

export function loadPuzzleState(
  puzzleId: string,
  difficulty: number
): PuzzleStateMap | null {
  const raw = getJSON<Record<string, PieceStateEntry>>(storageKey(puzzleId, difficulty));
  if (!raw || typeof raw !== 'object') return null;
  const result: PuzzleStateMap = {};
  for (const [k, v] of Object.entries(raw)) {
    const id = Number(k);
    if (Number.isNaN(id) || !v || typeof v.where !== 'string') continue;
    if (v.where !== 'tray' && v.where !== 'board') continue;
    result[id] = {
      where: v.where,
      x: typeof v.x === 'number' ? v.x : undefined,
      y: typeof v.y === 'number' ? v.y : undefined,
      placedCorrect: Boolean(v.placedCorrect),
    };
  }
  return Object.keys(result).length > 0 ? result : null;
}

export function savePuzzleState(
  puzzleId: string,
  difficulty: number,
  state: PuzzleStateMap
): boolean {
  const payload: Record<string, PieceStateEntry> = {};
  for (const [id, entry] of Object.entries(state)) {
    payload[String(id)] = entry;
  }
  return setJSON(storageKey(puzzleId, difficulty), payload);
}

/** Удалить сохранённый прогресс пазла (после завершения). */
export function clearPuzzleState(puzzleId: string, difficulty: number): void {
  remove(storageKey(puzzleId, difficulty));
  remove(trayOrderStorageKey(puzzleId, difficulty));
}

export function loadPuzzleTrayOrder(
  puzzleId: string,
  difficulty: number
): number[] | null {
  const raw = getJSON<unknown>(trayOrderStorageKey(puzzleId, difficulty));
  if (!Array.isArray(raw)) return null;
  const ids = raw.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  return ids.length > 0 ? ids : null;
}

export function savePuzzleTrayOrder(
  puzzleId: string,
  difficulty: number,
  pieceIds: number[]
): boolean {
  const payload = pieceIds.filter((id) => Number.isFinite(id));
  return setJSON(trayOrderStorageKey(puzzleId, difficulty), payload);
}
